import { Injectable, OnDestroy } from '@angular/core';

/**
 * SessionTracker — BroadcastChannel-based tab detection.
 *
 * Uses a simple ping/pong protocol over a BroadcastChannel to detect
 * whether another browser tab is running the same application.
 *
 * The async method resolves within 100ms:
 *   - true  if another tab responds with 'pong'
 *   - false on timeout (no other tab)
 */
@Injectable({ providedIn: 'root' })
export class SessionTracker implements OnDestroy {
  private channel = new BroadcastChannel('roadit_tab_sync');
  private instanceId = Math.random().toString(36).substring(2);

  constructor() {
    this.channel.onmessage = (event) => this.handleMessage(event);
  }

  /**
   * Returns a Promise that resolves to `true` if another tab responds
   * with a 'pong' within 100ms, `false` otherwise.
   */
  isAnotherTabOpen(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.channel.postMessage({ type: 'ping', senderId: this.instanceId });

      const timeout = setTimeout(() => resolve(false), 100);

      // Temporary one-shot listener for the 'pong' targeted at us
      const handler = (event: MessageEvent) => {
        if (event.data.type === 'pong' && event.data.targetId === this.instanceId) {
          clearTimeout(timeout);
          resolve(true);
        }
      };

      this.channel.addEventListener('message', handler);

      // Clean up the one-shot listener after the timeout window
      setTimeout(() => {
        this.channel.removeEventListener('message', handler);
      }, 100);
    });
  }

  /**
   * Respond to 'ping' messages from other tabs.
   * Always registered so the service is responsive even when not actively querying.
   */
  private handleMessage(event: MessageEvent): void {
    if (event.data.type === 'ping' && event.data.senderId !== this.instanceId) {
      this.channel.postMessage({ type: 'pong', targetId: event.data.senderId });
    }
  }

  ngOnDestroy(): void {
    this.channel.close();
  }
}
